import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Calculator, Trash2, FolderPlus, ArrowLeft, Sparkles, Package, HardHat, Clock } from 'lucide-react';

const QuickCalcArchive = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.listQuickCalcArchive();
      setItems(res.data);
    } catch {
      toast.error('Kunde inte hämta arkiv');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ta bort denna beräkning?')) return;
    try {
      await api.deleteQuickCalc(id);
      toast.success('Borttagen');
      fetchData();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleConvert = async (id, name) => {
    try {
      const res = await api.quickCalcToProject(id, { project_name: name });
      toast.success(`Projekt skapat: ${res.data.project_name}`);
      navigate(`/dashboard/projects/${res.data.project_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa projekt');
    }
  };

  const opened = items.find((i) => i.id === openId);

  return (
    <div className="space-y-4" data-testid="quick-calc-archive">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/dashboard')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1.5">
            <ArrowLeft size={12} /> Tillbaka
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator size={22} className="text-[#E8613A]" /> Snabb-arkiv
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Sparade AI-totalberäkningar. Du kan göra om dem till projekt.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground p-6 text-center">Laddar...</p>
      ) : items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-xl p-8 text-center">
          <Calculator size={36} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">Inga sparade beräkningar än. Kör en totalberäkning från Dashboard och spara resultatet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <article key={it.id} className="bg-card border border-border rounded-xl p-4 hover:border-[#E8613A]/40 transition-colors flex flex-col" data-testid={`archive-item-${it.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-[#E8613A] uppercase tracking-widest font-bold flex items-center gap-1"><Sparkles size={9} /> AI Total</p>
                  <h3 className="text-sm font-bold mt-0.5 truncate">{it.name}</h3>
                </div>
                <button onClick={() => handleDelete(it.id)} className="text-muted-foreground hover:text-red-600 p-1" data-testid={`del-${it.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{it.description}</p>
              <div className="text-xl font-bold text-foreground mb-3">
                {((it.result?.total_cost) || 0).toLocaleString('sv-SE')} <span className="text-xs font-normal text-muted-foreground">kr totalt</span>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground mb-3 pt-2 border-t border-border">
                <span title="Material"><Package size={11} className="inline mr-0.5" />{((it.result?.material?.total_cost) || 0).toLocaleString('sv-SE')}</span>
                <span title="Arbete"><HardHat size={11} className="inline mr-0.5" />{((it.result?.labor?.total_cost) || 0).toLocaleString('sv-SE')}</span>
                <span title="Tid"><Clock size={11} className="inline mr-0.5" />{it.result?.time?.estimated_days || 0}d</span>
              </div>
              <div className="flex gap-2 mt-auto">
                <Button size="sm" variant="outline" onClick={() => setOpenId(it.id)} className="flex-1 h-8 text-xs" data-testid={`view-${it.id}`}>Visa</Button>
                {!it.converted_to_project_id ? (
                  <Button size="sm" onClick={() => handleConvert(it.id, it.name)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-8 text-xs" data-testid={`convert-${it.id}`}>
                    <FolderPlus size={11} className="mr-1" /> Projekt
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/projects/${it.converted_to_project_id}`)} className="h-8 text-xs">
                    Öppna projekt
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {opened && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={() => setOpenId(null)}>
          <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="archive-detail">
            <h2 className="text-xl font-bold">{opened.name}</h2>
            <p className="text-sm text-muted-foreground mt-2">{opened.description}</p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="bg-muted/30 rounded-lg p-2"><p className="text-[10px] uppercase text-muted-foreground">Material</p><p className="font-bold">{((opened.result?.material?.total_cost) || 0).toLocaleString('sv-SE')} kr</p></div>
              <div className="bg-muted/30 rounded-lg p-2"><p className="text-[10px] uppercase text-muted-foreground">Arbete</p><p className="font-bold">{((opened.result?.labor?.total_cost) || 0).toLocaleString('sv-SE')} kr</p></div>
              <div className="bg-muted/30 rounded-lg p-2"><p className="text-[10px] uppercase text-muted-foreground">Tid</p><p className="font-bold">{opened.result?.time?.estimated_days || 0} d</p></div>
            </div>
            <p className="text-sm mt-4 text-foreground bg-[#E8613A]/5 border border-[#E8613A]/20 rounded-lg p-3">{opened.result?.summary}</p>
            <Button onClick={() => setOpenId(null)} variant="outline" className="mt-4 rounded-full">Stäng</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickCalcArchive;
